<template>
  <div class="home-page">
    <ToastNotify />

    <div class="home-bg">
      <span class="orb orb-1"></span>
      <span class="orb orb-2"></span>
      <span class="orb orb-3"></span>
    </div>

    <!-- UUID 输入 -->
    <div v-if="view === 'form'" class="form-shell">
      <div class="form-card">
        <div class="form-header">
          <div class="logo">✉</div>
          <h1>临时邮箱</h1>
          <p>请输入您的 UUID 访问邮箱</p>
        </div>

        <form class="uuid-form" @submit.prevent="handleSubmit">
          <div class="form-item">
            <label for="uuid">UUID</label>
            <input
              id="uuid"
              v-model="uuidInput"
              type="text"
              class="dark-input"
              placeholder="请输入 UUID"
              :disabled="loading"
            />
          </div>

          <p v-if="errorMsg" class="error-msg">{{ errorMsg }}</p>

          <button class="btn btn-primary btn-block" type="submit" :disabled="loading">
            <span v-if="loading" class="spinner"></span>
            {{ loading ? '验证中...' : '进 入' }}
          </button>
        </form>
      </div>
    </div>

    <!-- 收件箱 -->
    <div v-else class="inbox-shell">
      <header class="page-header">
        <div class="header-left">
          <div class="logo logo-sm">✉</div>
          <div>
            <h1>{{ account.account }}</h1>
            <p>临时邮箱 · 收件箱</p>
          </div>
        </div>
        <div class="header-actions">
          <button class="btn-copy" @click="copyText(account.account)">复制邮箱</button>
          <button class="btn-ghost" @click="handleSwitchAccount">切换账号</button>
        </div>
      </header>

      <div class="inbox-body">
        <!-- 左侧邮件列表 -->
        <aside class="mail-list-panel">
          <div class="list-header">
            <span class="list-title">收件箱</span>
            <span class="list-badge">{{ emails.length }}</span>
          </div>

          <div class="list-scroll theme-scroll">
            <div v-if="emailsLoading && emails.length === 0" class="list-empty">加载中...</div>
            <div v-else-if="emails.length === 0" class="list-empty">暂无邮件</div>
            <button
              v-for="item in emails"
              :key="item.id"
              type="button"
              class="mail-item"
              :class="{ active: selectedId === item.id }"
              @click="selectEmail(item.id)"
            >
              <div class="mail-item-subject">{{ item.subject || '（无主题）' }}</div>
              <div class="mail-item-from">{{ formatFromName(item) }}</div>
              <div class="mail-item-time">{{ formatListTime(item) }}</div>
            </button>
          </div>
        </aside>

        <!-- 右侧邮件详情 -->
        <section class="mail-detail-panel">
          <h2 class="detail-title">邮件详情</h2>

          <div v-if="!selectedEmail" class="detail-empty">请选择一封邮件查看详情</div>

          <template v-else>
            <div class="detail-meta">
              <div class="meta-row">
                <span class="meta-label">主题</span>
                <span class="meta-value">{{ selectedEmail.subject || '（无主题）' }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">发件人</span>
                <span class="meta-value">{{ formatFrom(selectedEmail) }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">收件人</span>
                <span class="meta-value">{{ formatTo(selectedEmail) }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">时间</span>
                <span class="meta-value">{{ formatListTime(selectedEmail) }}</span>
              </div>
            </div>

            <div class="view-toggle">
              <button
                type="button"
                class="toggle-btn"
                :class="{ active: contentMode === 'text' }"
                @click="contentMode = 'text'"
              >纯文本</button>
              <button
                type="button"
                class="toggle-btn"
                :class="{ active: contentMode === 'html' }"
                :disabled="!selectedEmail.html"
                @click="contentMode = 'html'"
              >HTML</button>
            </div>

            <div class="detail-divider"></div>

            <div class="mail-body-wrap">
              <div v-if="detailLoading" class="detail-loading">加载中...</div>
              <pre v-else-if="contentMode === 'text'" class="mail-body-text theme-scroll">{{ selectedEmail.text || '（无正文）' }}</pre>
              <div
                v-else
                class="mail-body-html theme-scroll"
                v-html="selectedEmail.html || '（无 HTML 内容）'"
              ></div>
            </div>
          </template>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { getAccount, getEmails, getEmail } from '../api'
import { notify } from '../utils/notify'
import { copyToClipboard } from '../utils/clipboard'
import ToastNotify from './ToastNotify.vue'

const POLL_INTERVAL = 3000

export default {
  name: 'HomePage',
  components: {
    ToastNotify
  },
  data() {
    return {
      view: 'form',
      uuidInput: '',
      currentUuid: '',
      account: null,
      loading: false,
      errorMsg: '',
      emails: [],
      emailsLoading: false,
      selectedId: null,
      selectedEmail: null,
      detailLoading: false,
      contentMode: 'text',
      pollTimer: null
    }
  },
  mounted() {
    const uuid = this.getUuidFromUrl()
    if (uuid) {
      this.uuidInput = uuid
      this.fetchAccount(uuid)
    }
  },
  beforeUnmount() {
    this.stopPolling()
  },
  methods: {
    getUuidFromUrl() {
      return new URLSearchParams(window.location.search).get('uuid')?.trim() || ''
    },
    setUuidInUrl(uuid) {
      const url = new URL(window.location.href)
      if (uuid) {
        url.searchParams.set('uuid', uuid)
      } else {
        url.searchParams.delete('uuid')
      }
      window.history.pushState({}, '', url)
    },
    showFormWithError(message) {
      this.stopPolling()
      this.view = 'form'
      this.account = null
      this.currentUuid = ''
      this.emails = []
      this.selectedId = null
      this.selectedEmail = null
      this.errorMsg = message
    },
    handleSwitchAccount() {
      this.stopPolling()
      this.view = 'form'
      this.account = null
      this.currentUuid = ''
      this.emails = []
      this.selectedId = null
      this.selectedEmail = null
      this.uuidInput = ''
      this.errorMsg = ''
      this.setUuidInUrl('')
    },
    async fetchAccount(uuid) {
      this.loading = true
      this.errorMsg = ''

      try {
        const res = await getAccount(uuid)
        if (res && res.account) {
          this.account = res
          this.currentUuid = uuid
          this.view = 'inbox'
          this.startPolling()
        } else {
          this.showFormWithError('UUID 错误')
        }
      } catch {
        this.showFormWithError('UUID 错误')
      } finally {
        this.loading = false
      }
    },
    async handleSubmit() {
      const uuid = this.uuidInput.trim()
      if (!uuid) {
        this.errorMsg = '请输入 UUID'
        return
      }

      this.setUuidInUrl(uuid)
      await this.fetchAccount(uuid)
    },
    startPolling() {
      this.stopPolling()
      this.fetchEmails()
      this.pollTimer = setInterval(this.fetchEmails, POLL_INTERVAL)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    async fetchEmails() {
      if (!this.currentUuid) return

      this.emailsLoading = true
      try {
        const list = await getEmails(this.currentUuid)
        const emails = (Array.isArray(list) ? list : []).sort((a, b) => {
          const ta = new Date(a.received || a.date || 0).getTime()
          const tb = new Date(b.received || b.date || 0).getTime()
          return tb - ta
        })
        this.emails = emails

        if (this.selectedId) {
          const stillExists = emails.some((e) => e.id === this.selectedId)
          if (!stillExists) {
            this.selectedId = null
            this.selectedEmail = null
          }
        }

        if (!this.selectedId && emails.length > 0) {
          await this.selectEmail(emails[0].id)
        } else if (this.selectedId) {
          const current = emails.find((e) => e.id === this.selectedId)
          if (current && this.selectedEmail) {
            this.selectedEmail = { ...this.selectedEmail, ...current }
          }
        }
      } catch {
        // 轮询失败不打断页面
      } finally {
        this.emailsLoading = false
      }
    },
    async selectEmail(id) {
      if (!id || !this.currentUuid) return

      this.selectedId = id
      this.contentMode = 'text'
      this.detailLoading = true

      try {
        const email = await getEmail(this.currentUuid, id)
        this.selectedEmail = email
        if (email.html && !email.text) {
          this.contentMode = 'html'
        }
      } catch (err) {
        this.selectedEmail = null
        notify.error(err.message || '加载邮件失败')
      } finally {
        this.detailLoading = false
      }
    },
    formatFromName(item) {
      if (item.from_name) return item.from_name
      if (item.from_addr) return item.from_addr
      return item.from || '未知发件人'
    },
    formatFrom(item) {
      if (item.from_name && item.from_addr) {
        return `${item.from_name} <${item.from_addr}>`
      }
      return item.from || item.from_addr || '—'
    },
    formatTo(item) {
      if (item.to && item.to.length > 0) {
        return item.to.join(', ')
      }
      if (item.to_addrs && item.to_addrs.length > 0) {
        return item.to_addrs.join(', ')
      }
      return this.account?.account || '—'
    },
    formatListTime(item) {
      const raw = item.received || item.date
      if (!raw) return '—'
      try {
        const date = new Date(raw)
        if (Number.isNaN(date.getTime())) return String(raw)
        const y = date.getFullYear()
        const m = date.getMonth() + 1
        const d = date.getDate()
        const h = String(date.getHours()).padStart(2, '0')
        const min = String(date.getMinutes()).padStart(2, '0')
        const s = String(date.getSeconds()).padStart(2, '0')
        return `${y}/${m}/${d} ${h}:${min}:${s}`
      } catch {
        return String(raw)
      }
    },
    async copyText(text) {
      if (!text) return
      try {
        await copyToClipboard(text)
        notify.success('已复制到剪贴板')
      } catch {
        notify.error('复制失败')
      }
    }
  }
}
</script>

<style scoped>
.home-page {
  position: relative;
  height: 100%;
  overflow: hidden;
  background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0c4a6e 100%);
}

.home-bg {
  position: fixed;
  inset: 0;
  pointer-events: none;
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.45;
}

.orb-1 {
  width: 360px;
  height: 360px;
  top: -100px;
  left: -80px;
  background: #38bdf8;
}

.orb-2 {
  width: 300px;
  height: 300px;
  right: -60px;
  top: 20%;
  background: #818cf8;
}

.orb-3 {
  width: 240px;
  height: 240px;
  bottom: -80px;
  left: 35%;
  background: #22d3ee;
}

/* UUID 表单 */
.form-shell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  overflow-y: auto;
  padding: 24px;
}

.form-card {
  width: 100%;
  max-width: 420px;
  padding: 40px 36px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.35);
  animation: fadeUp 0.6s ease-out;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.form-header {
  text-align: center;
  margin-bottom: 32px;
}

.logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
  font-size: 32px;
  border-radius: 16px;
  background: linear-gradient(135deg, #38bdf8, #6366f1);
  box-shadow: 0 8px 24px rgba(56, 189, 248, 0.35);
}

.form-header h1 {
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 600;
  color: #f8fafc;
  letter-spacing: 1px;
}

.form-header p {
  margin: 0;
  font-size: 14px;
  color: #94a3b8;
}

.uuid-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-item label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #cbd5e1;
  text-align: left;
}

.dark-input {
  width: 100%;
  height: 46px;
  padding: 0 14px;
  font-size: 15px;
  color: #f1f5f9;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 10px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}

.dark-input::placeholder {
  color: #64748b;
}

.dark-input:focus {
  border-color: #38bdf8;
  box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.2);
}

.dark-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-msg {
  margin: 0;
  padding: 10px 14px;
  font-size: 13px;
  color: #fca5a5;
  text-align: left;
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.25);
  border-radius: 8px;
}

.btn {
  height: 48px;
  padding: 0 18px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.15s, opacity 0.2s, box-shadow 0.2s;
}

.btn-primary {
  color: #fff;
  letter-spacing: 2px;
  background: linear-gradient(135deg, #0ea5e9, #6366f1);
  box-shadow: 0 8px 20px rgba(14, 165, 233, 0.35);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px rgba(14, 165, 233, 0.45);
}

.btn-block {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
}

.btn:disabled {
  opacity: 0.75;
  cursor: not-allowed;
  transform: none;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 收件箱 */
.inbox-shell {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 24px 28px 28px;
  box-sizing: border-box;
  overflow: hidden;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  margin-bottom: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.logo-sm {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 0;
  font-size: 24px;
  border-radius: 12px;
}

.page-header h1 {
  margin: 0 0 2px;
  font-size: 18px;
  font-weight: 600;
  color: #f8fafc;
  letter-spacing: 0.5px;
  word-break: break-all;
}

.page-header p {
  margin: 0;
  font-size: 13px;
  color: #94a3b8;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.btn-copy {
  flex-shrink: 0;
  height: 38px;
  padding: 0 14px;
  font-size: 13px;
  color: #cbd5e1;
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 8px;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s, background 0.2s;
}

.btn-copy:hover {
  color: #f8fafc;
  border-color: rgba(56, 189, 248, 0.4);
  background: rgba(14, 165, 233, 0.15);
}

.btn-ghost {
  height: 38px;
  padding: 0 14px;
  font-size: 13px;
  color: #cbd5e1;
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 8px;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s, background 0.2s;
}

.btn-ghost:hover {
  color: #f8fafc;
  border-color: rgba(56, 189, 248, 0.4);
  background: rgba(14, 165, 233, 0.15);
}

.inbox-body {
  display: flex;
  flex: 1;
  min-height: 0;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  backdrop-filter: blur(20px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

/* 左侧列表 */
.mail-list-panel {
  display: flex;
  flex-direction: column;
  width: 300px;
  flex-shrink: 0;
  min-height: 0;
  border-right: 1px solid rgba(148, 163, 184, 0.15);
}

.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 16px 18px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.15);
}

.list-title {
  font-size: 15px;
  font-weight: 600;
  color: #f1f5f9;
}

.list-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #0ea5e9, #6366f1);
  border-radius: 11px;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.3);
}

.list-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.list-empty {
  padding: 32px 16px;
  font-size: 13px;
  color: #64748b;
  text-align: center;
}

.mail-item {
  display: block;
  width: 100%;
  padding: 12px 18px;
  text-align: left;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  cursor: pointer;
  transition: background 0.15s;
}

.mail-item:hover {
  background: rgba(56, 189, 248, 0.08);
}

.mail-item.active {
  background: linear-gradient(135deg, rgba(14, 165, 233, 0.2), rgba(99, 102, 241, 0.2));
}

.mail-item-subject {
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 500;
  color: #f1f5f9;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mail-item-from {
  margin-bottom: 2px;
  font-size: 13px;
  color: #94a3b8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mail-item-time {
  font-size: 12px;
  color: #64748b;
}

/* 右侧详情 */
.mail-detail-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  padding: 20px 24px;
  overflow: hidden;
}

.detail-title {
  flex-shrink: 0;
  margin: 0 0 18px;
  font-size: 15px;
  font-weight: 600;
  color: #f1f5f9;
}

.detail-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #64748b;
}

.detail-meta {
  flex-shrink: 0;
  margin-bottom: 16px;
}

.meta-row {
  display: flex;
  gap: 12px;
  margin-bottom: 10px;
  font-size: 14px;
  line-height: 1.6;
}

.meta-row:last-child {
  margin-bottom: 0;
}

.meta-label {
  flex-shrink: 0;
  width: 48px;
  color: #94a3b8;
}

.meta-value {
  flex: 1;
  min-width: 0;
  color: #e2e8f0;
  word-break: break-all;
}

.view-toggle {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-bottom: 16px;
}

.toggle-btn {
  height: 34px;
  padding: 0 16px;
  font-size: 13px;
  font-weight: 500;
  color: #cbd5e1;
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 8px;
  cursor: pointer;
  transition: color 0.2s, background 0.2s, border-color 0.2s, box-shadow 0.2s;
}

.toggle-btn:hover:not(:disabled) {
  color: #f8fafc;
  border-color: rgba(56, 189, 248, 0.4);
}

.toggle-btn.active {
  color: #fff;
  background: linear-gradient(135deg, #0ea5e9, #6366f1);
  border-color: transparent;
  box-shadow: 0 4px 14px rgba(14, 165, 233, 0.3);
}

.toggle-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.detail-divider {
  flex-shrink: 0;
  height: 1px;
  margin-bottom: 16px;
  background: rgba(148, 163, 184, 0.15);
}

.mail-body-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.detail-loading {
  padding: 24px;
  font-size: 14px;
  color: #64748b;
  text-align: center;
}

.mail-body-text {
  flex: 1;
  min-height: 0;
  margin: 0;
  padding: 16px 18px;
  overflow-y: auto;
  overflow-x: hidden;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 14px;
  line-height: 1.7;
  color: #e2e8f0;
  white-space: pre-wrap;
  word-break: break-word;
  background: rgba(15, 23, 42, 0.55);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 10px;
  box-sizing: border-box;
}

.mail-body-html {
  flex: 1;
  min-height: 0;
  padding: 16px 18px;
  overflow-y: auto;
  overflow-x: hidden;
  font-size: 14px;
  line-height: 1.7;
  color: #e2e8f0;
  background: rgba(15, 23, 42, 0.55);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 10px;
  box-sizing: border-box;
}

/* 内部滚动条样式 */
.theme-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(56, 189, 248, 0.45) rgba(15, 23, 42, 0.35);
}

.theme-scroll::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.theme-scroll::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.35);
  border-radius: 4px;
}

.theme-scroll::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, rgba(56, 189, 248, 0.5), rgba(99, 102, 241, 0.5));
  border: 2px solid transparent;
  border-radius: 4px;
  background-clip: padding-box;
}

.theme-scroll::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, rgba(56, 189, 248, 0.75), rgba(99, 102, 241, 0.75));
  border: 2px solid transparent;
  background-clip: padding-box;
}

.theme-scroll::-webkit-scrollbar-corner {
  background: transparent;
}

@media (max-width: 768px) {
  .inbox-shell {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .inbox-body {
    flex-direction: column;
  }

  .mail-list-panel {
    width: 100%;
    max-height: 40vh;
    border-right: none;
    border-bottom: 1px solid rgba(148, 163, 184, 0.15);
  }

  .mail-detail-panel {
    flex: 1;
    min-height: 0;
    padding: 16px;
  }

  .form-card {
    padding: 32px 24px;
  }
}
</style>
